// Main application entry point

import './styles/globals.css';
import { initAuth } from './firebase.ts';
import {
  initStore,
  dispatch,
  subscribe,
  getState,
  resetStore,
} from './state/session-store.ts';
import { Phase, type SessionState } from './state/types.ts';
import { getProgress } from './state/machine.ts';
import { screens } from './screens/index.ts';
import type { ScreenContext } from './screens/types.ts';
import { audioManager } from './audio/audio-manager.ts';
import { listClips, getGuidanceTemplates, abandonSession } from './api/operations.ts';
import { preloadClipUrls } from './api/storage.ts';
import { renderProgressBar } from './components/progress-bar.ts';
import { setClipPools } from './screens/helpers.ts';

const clipUrlMap = new Map<string, string>();

// Initialize the application
async function init() {
  console.log('🎵 The emotional echo of music  - Initializing...');

  try {
    const uid = await initAuth();
    if (!uid) {
      console.warn(
        'Startup continued without Firebase auth. Storage reads may fail if your rules require auth.'
      );
    }
    initStore();

    const [clips, templates] = await Promise.all([listClips(), getGuidanceTemplates()]);
    setClipPools(clips);

    // Always provide a synchronous fallback URL so startup never blocks on storage.
    clips.forEach((clip) => clipUrlMap.set(clip.id, clip.filename));

    dispatch({ type: 'SET_CLIPS', payload: clips });
    dispatch({ type: 'SET_GUIDANCE_TEMPLATES', payload: templates });

    // Subscribe to state changes
    subscribe((state) => renderApp(state));

    renderApp(getState());

    // Best-effort abandonment on tab close
    window.addEventListener('beforeunload', () => {
      const state = getState();
      if (state.sessionId && state.phase !== Phase.GUIDANCE) {
        abandonSession(state.sessionId).catch(() => {});
      }
    });

    // Resolve signed storage URLs in background; never block initial UI render.
    if (uid) {
      preloadClipUrls(clips.map((c) => c.filename))
        .then((urlMap) => {
          clips.forEach((clip) => {
            const resolved = urlMap.get(clip.filename);
            if (resolved) {
              clipUrlMap.set(clip.id, resolved);
            }
          });
        })
        .catch((err) => {
          console.warn('Clip URL background preload failed, continuing with fallback filenames', err);
        });
    }

    console.log('✅ Application initialized successfully');
  } catch (error) {
    console.error('❌ Failed to initialize application:', error);
    showError('Failed to initialize application. Please refresh the page.');
  }
}

// Render the application based on current state
function renderApp(state: SessionState) {
  const appRoot = document.getElementById('app');
  if (!appRoot) return;

  appRoot.innerHTML = '';

  const shell = document.createElement('div');
  shell.className =
    'min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4';

  const frame = document.createElement('div');
  frame.className = 'max-w-md w-full';

  // App header
  const header = document.createElement('div');
  header.className = 'mb-4';

  const titleRow = document.createElement('div');
  titleRow.className = 'flex items-center justify-between';

  const title = document.createElement('div');
  title.innerHTML = `
    <div class="text-xs uppercase tracking-wide text-gray-500">Music & Mood Session</div>
    <div class="text-2xl font-bold text-gray-900">The emotional echo of music </div>
  `;

  const progress = renderProgressBar(getSessionProgress(state));
  progress.classList.add('mt-3');

  titleRow.appendChild(title);
  header.appendChild(titleRow);
  header.appendChild(progress);

  const body = document.createElement('div');
  body.id = 'screen-container';

  frame.appendChild(header);
  frame.appendChild(body);
  shell.appendChild(frame);
  appRoot.appendChild(shell);

  const screenRenderer = screens[state.phase];
  const ctx: ScreenContext = { state, dispatch, audioManager, clipUrlMap };

  if (screenRenderer) {
    screenRenderer(body, ctx);
  } else {
    body.innerHTML = `<div class="card">Unknown phase: ${state.phase}</div>`;
  }
}

// Better progress indicator (roughly mapped to flow)
function getSessionProgress(state: SessionState): number {
  const base = getProgress(state.phase);
  if (state.phase === Phase.CLIP_CHECKIN) {
    return base + state.clipIndex * 5;
  }
  if (state.phase === Phase.SILENCE_CHECKIN) {
    return base + state.silenceIndex * 5;
  }
  return base;
}

// Show error message
function showError(message: string) {
  const appRoot = document.getElementById('app');
  if (appRoot) {
    appRoot.innerHTML = `
      <div class="min-h-screen flex items-center justify-center p-4">
        <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 class="text-red-800 text-xl font-semibold mb-2">Error</h2>
          <p class="text-red-600">${message}</p>
        </div>
      </div>
    `;
  }
}

// Expose reset for debugging
(window as any).resetSession = resetStore;

// Start the application
init();
